import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  GetRequestStatusTenant,
  GetRequestTenant,
} from "../../../services/tenantrequest";
import { subscribeTenantChange } from "../../../utils/tenantChangeEvent";
import { resolveImageUrl, formatStatusLabel } from "../../../utils";

interface DropdownData {
  DataId: number;
  DataLabel: string;
  Description: string | null;
}

interface ApiResponse {
  dropdownClassDatas: DropdownData[];
  TotalRecord: number;
  Code: number;
  Message: string;
}

interface TenantRequestData {
  TenantRequestId: number;
  RequestNumber: string;
  MallName: string;
  TenantName: string;
  Notes: string;
  RequestStatusName?: string;
  RequestStatusId?: number;
  Status: number;
  CreatedOn?: string;
}

interface StatusCount {
  statusId: number;
  statusLabel: string;
  count: number;
}

// ✅ Terima selectedTenant & selectedMall sebagai props dari Home
interface TenantRequestSectionProps {
  selectedTenant?: number | null;
  selectedMall?: number | null;
}

const getIconForStatus = (label: string) => {
  const lowerLabel = label.toLowerCase();

  if (lowerLabel.includes("new")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  if (lowerLabel.includes("inspection") || lowerLabel.includes("insper")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    );
  }
  if (lowerLabel.includes("waiting") || lowerLabel.includes("wait")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (lowerLabel.includes("approval") || lowerLabel.includes("approve")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  if (lowerLabel.includes("done") || lowerLabel.includes("complete")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (lowerLabel.includes("reject")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    );
  }
  if (lowerLabel.includes("close") || lowerLabel.includes("closed")) {
    return (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

export default function TenantRequestSection({ selectedTenant, selectedMall }: TenantRequestSectionProps) {
  const [, setStatuses] = useState<DropdownData[]>([]);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchData = async () => {
    if (isFetchingRef.current) return;

    // Baca dari storage agar selalu dapat nilai terbaru saat dipanggil dari event
    const tenantId = selectedTenant
      ?? Number(localStorage.getItem("tenant_id") ?? sessionStorage.getItem("tenant_id") ?? 0);
    const mallId = selectedMall
      ?? Number(localStorage.getItem("mall_id") ?? sessionStorage.getItem("mall_id") ?? 0);

    isFetchingRef.current = true;
    try {
      setLoading(true);
      setError(null);

      const statusRes: ApiResponse = await GetRequestStatusTenant(0);
      if (!statusRes?.dropdownClassDatas) {
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      setStatuses(statusRes.dropdownClassDatas);

      if (!tenantId || !mallId || tenantId <= 0 || mallId <= 0) {
        const counts: StatusCount[] = statusRes.dropdownClassDatas
          .filter((status) => status.DataId !== 60)
          .map((status) => ({
            statusId: status.DataId,
            statusLabel: status.DataLabel,
            count: 0,
          }));
        setStatusCounts(counts);
        return;
      }

      const requestRes = await GetRequestTenant(tenantId, 0, mallId, 0, 0, 0, 1);

      if (requestRes?.requestTenantDatas && Array.isArray(requestRes.requestTenantDatas)) {
        const requestData: TenantRequestData[] = requestRes.requestTenantDatas;
        const counts: StatusCount[] = statusRes.dropdownClassDatas
          .filter((status) => status.DataId !== 60)
          .map((status) => ({
            statusId: status.DataId,
            statusLabel: status.DataLabel,
            count: requestData.filter(
              (req) => Number(req.RequestStatusId) === Number(status.DataId)
            ).length,
          }));
        setStatusCounts(counts);
      } else {
        const counts: StatusCount[] = statusRes.dropdownClassDatas
          .filter((status) => status.DataId !== 60)
          .map((status) => ({
            statusId: status.DataId,
            statusLabel: status.DataLabel,
            count: 0,
          }));
        setStatusCounts(counts);
      }
    } catch (err) {
      console.error("Error fetching tenant request data:", err);
      setError("Failed to load status data");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch ulang saat tenant berubah via dropdown
  useEffect(() => {
    const handleTenantChange = () => {
      fetchData();
    };

    const unsubscribe = subscribeTenantChange(handleTenantChange);
    return unsubscribe;
  }, []);

  return (
    <div className="relative rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200 overflow-hidden dark:bg-gray-900 dark:ring-gray-700 min-h-44 pb-8">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Tenant Request
        </h3>
        <Link
          to="/tenantRequest"
          className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-white flex items-center gap-1"
        >
          View all <span>{"\u203A"}</span>
        </Link>
      </div>

      <div className="relative z-10 pr-28">
        {loading ? (
          <div className="flex items-center justify-center h-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-sm text-red-500">{error}</div>
        ) : (
          <div className="grid grid-cols-3 gap-x-4 gap-y-4">
            {statusCounts.length === 0 ? (
              <p className="col-span-3 text-sm text-gray-500 dark:text-gray-400">
                No data available
              </p>
            ) : (
              statusCounts.map((item) => (
                <div key={item.statusId} className="flex flex-col items-center text-center gap-1">
                  <div className="w-9 h-9 rounded-lg border border-blue-100 bg-white dark:bg-gray-800 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                    {getIconForStatus(item.statusLabel)}
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight line-clamp-2">
                      {formatStatusLabel(item.statusLabel)}
                    </p>
                    <p className="text-base font-bold text-gray-900 dark:text-white leading-tight mt-0.5">
                      {item.count}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="absolute bottom-2 right-2 w-24 h-24 pointer-events-none">
        <img
          src={resolveImageUrl("/images/icon (5).png")}
          alt="Document clipboard"
          className="w-full h-full object-contain"
        />
      </div>
    </div>
  );
}